import React, { useEffect, useRef, useState } from 'react';
import { capitalize, get } from 'lodash';
import { AutoComplete, Button, Col, Form, Input, Row, Tabs } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { createLocation, fetchContacts, fetchDistricts, fetchRegions } from '../../offices/offices-actions';
import { getDistricts, getLocationDetails, getRegions, getSearchRegionId } from '../../offices/offices-selectors';
import { locationConfig } from '../configs/location-address-config';
import { formItemMap } from '@med-fe/ui';
import { StatusesEnum } from '../../../models/statuses-enum';
import { getAdminEmails, getWriteTabsPermissions } from '../../../store/user/user-reducer';
import { getIsWriteAllAllowed } from '../../../store/user/user-reducer';
import { LocationCategory } from 'apps/mod/src/models/locationCategory-enum';
import { PosEnum } from 'apps/mod/src/models/pos-enum';
import moment from 'moment';
import { useMsal } from '@azure/msal-react';
import { fetchAdminEmails } from 'apps/mod/src/store/user/user-actions';
import { getAssignmentCount, getContacts } from '../../offices/offices-selectors';

interface ILocationDetailsProps {
  locId?: string;
  isCreateMode?: boolean;
  onClose?: any;
}

function LocationDetails({ locId, isCreateMode = false, onClose }: ILocationDetailsProps) {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const adminEmails = useSelector(getAdminEmails);
  const [allowDateClear, setAllowDateClear] = useState<boolean>(false);
  const hasMounted = useRef(false);
  const { accounts } = useMsal();
  const contacts = useSelector(getContacts);

  useEffect(() => {
    if (hasMounted.current) return;
    hasMounted.current = true;
    dispatch(fetchAdminEmails());
  }, []);

  useEffect(() => {
    setAllowDateClear(adminEmails?.some((user) => user?.trim().toLowerCase() === accounts[0]?.username));
  }, [adminEmails, accounts]);

  const [regionId, setRegionId]: any = useState(null);
  const [districtId, setDistrictId]: any = useState(null);
  const [isDisabled, setIsDisabled]: any = useState(true);
  const [statusValue, setStatusValue]: any = useState(false);

  const [districtManager, setDistrictManager]: any = useState(null);
  const [hrBusinessPartner, setHrBusinessPartner]: any = useState(null);
  const [regionalVicePresident, setRegionalVicePresident]: any = useState(null);
  const name = 'location-details';
  const classNameForm = `${name}-form`;

  const regions = useSelector(getRegions);
  const districts = useSelector(getDistricts);
  const searchRegionId = useSelector(getSearchRegionId);
  const data: any = useSelector((state) => {
    if (locId) {
      // Edit mode
      return getLocationDetails(state, locId);
    } else {
      // Create mode
      return null;
    }
  });
  const tabWritePermissions = useSelector(getWriteTabsPermissions).locations;
  const isWriteAllAllowed = useSelector(getIsWriteAllAllowed);
  const isEditAllowed = tabWritePermissions; //|| isWriteAllAllowed;
  const disCheck = isEditAllowed && !isDisabled ? false : true;
  const [locationCategory, setLocationCategory] = useState(data?.locationCategory ? data?.locationCategory : null);
  const [pos, setpos] = useState(null);
  const [openingDate, setOpeningDate] = useState(data?.openingDate ? data?.openingDate : null);
  const [encompassLaunchDate, setEncompassLaunchDate] = useState(
    data?.encompassLaunchDate ? data?.encompassLaunchDate : null
  );
  const [isEncompassLaunchDateConfirmVisible, setEncompassLaunchDateConfirmVisible] = useState(false);
  const [isOpeningDateConfirmVisible, setOpeningDateConfirmVisible] = useState(false);
  const [isValidSelection, setIsValidSelection] = useState(false);
  const [isValidRVPSelection, setIsValidRVPSelection] = useState(false);
  const [isValidHRSelection, setIsValidHRSelection] = useState(false);

  useEffect(() => {
    if (isCreateMode) {
      form.setFieldsValue({ status: 'HOLD' });
      setStatusValue('HOLD');
      dispatch(fetchRegions());
    } else {
      form.setFieldsValue({
        ...data,
        ...(!!data.openingDate && { openingDate: moment(data.openingDate) }),
        status: get(data, 'status', null),
      });
      setRegionId(get(data, 'region', null));
      setStatusValue(get(data, 'status', null));
    }
    return () => {
      dispatch(fetchDistricts(searchRegionId));
      dispatch(fetchRegions());
      form.resetFields();
    };
  }, [locId]);

  useEffect(() => {
    if (regionId) {
      dispatch(fetchDistricts(regionId));
    }
  }, [regionId]);

  const onSubmit = (formData) => {
    const {
      region,
      district,
      county,
      status,
      locationName,
      locationNumber,
      directDial,
      timezone,
      ctc = false,
      openingDate,
      newOfficeYear,
      jointVentureYear,
      acquisitionYear,
      preAcquisitionAnnualRevenue,
      vintageName,
      pos,
      encompassLaunchDate,
      dryEyeTreatment = false,
      locationCategory,
      ...address
    } = formData;
    const isAddressTouched = form.isFieldsTouched(Object.keys(address));
    const fullAddress = { ...address, lng: data?.lng, lat: data?.lat, type: data?.type };
    interface Staff {
      job: string;
      id?: number | null;
    }

    const staff: Staff[] = [];

    if (districtManager) {
      staff.push({
        job: 'DM',
        id: districtManager.id,
      });
    }

    if (regionalVicePresident) {
      staff.push({
        job: 'RM',
        id: regionalVicePresident.id,
      });
    }

    if (hrBusinessPartner) {
      staff.push({
        job: 'HRBP',
        id: hrBusinessPartner.id,
      });
    }
    let payload = {
      ...(form.isFieldTouched('region') && (region ? { region: { id: region } } : { region: null })),
      ...(form.isFieldTouched('district') &&
        (district ? { district: { id: district } } : region && { district: null })),
      ...(county && form.isFieldTouched('county') && { county }),
      ...(directDial && form.isFieldTouched('directDial') && { directDial }),
      ...(timezone && form.isFieldTouched('timezone') && { timezone }),
      ...(ctc && form.isFieldTouched('ctc') && { ctc }),
      ...(openingDate && form.isFieldTouched('openingDate') && { openingDate }),
      ...(newOfficeYear && form.isFieldTouched('newOfficeYear') && { newOfficeYear }),
      ...(jointVentureYear && form.isFieldTouched('jointVentureYear') && { jointVentureYear }),
      ...(acquisitionYear && form.isFieldTouched('acquisitionYear') && { acquisitionYear }),
      ...(preAcquisitionAnnualRevenue &&
        form.isFieldTouched('preAcquisitionAnnualRevenue') && { preAcquisitionAnnualRevenue }),
      ...(vintageName && form.isFieldTouched('vintageName') && { vintageName }),
      ...(encompassLaunchDate && form.isFieldTouched('encompassLaunchDate') && { encompassLaunchDate }),
      ...(pos && form.isFieldTouched('pos') && { pos }),
      //...(status && form.isFieldTouched('status') && { status }),
      ...(dryEyeTreatment && form.isFieldTouched('dryEyeTreatment') && { dryEyeTreatment }),
      ...(locationCategory && form.isFieldTouched('locationCategory') && { locationCategory }),
      ...(isAddressTouched && {
        address: {
          id: get(data, 'id', null),
          ...fullAddress,
        },
      }),
      ...(staff.length > 0 && { staff }),
    };

    if (isCreateMode) {
      payload = {
        ...payload,
        locationName,
        locationNumber,
        status: status || 'HOLD',
        vintageName:
          locationCategory == 'DENOVO'
            ? newOfficeYear + ' De Novo'
            : locationCategory === 'JOINT_VENTURE'
            ? jointVentureYear + ' Joint Venture'
            : locationCategory === 'ACQUISITION'
            ? payload.vintageName
            : null,
      };
    } else {
      payload = {
        id: locId,
        ...payload,
        ...(status && form.isFieldTouched('status') && { status }),
        locationName,
      };
    }
    dispatch(createLocation(payload));
    setIsDisabled(true);
    if (isCreateMode) {
      onClose();
    }
  };

  function resetDistrict() {
    form.setFieldsValue({ district: null });
    form.validateFields(['district']);
  }

  const onOpeningDateSelect = (value) => {
    form.setFieldsValue({ openingDate: value ? value.format('YYYY-MM-DD') : null });
    setOpeningDate(value ? value.format('YYYY-MM-DD') : null);
    setIsDisabled(false);
  };

  const onEncompassLaunchDateSelect = (value) => {
    form.setFieldsValue({ encompassLaunchDate: value ? value.format('YYYY-MM-DD') : null });
    setEncompassLaunchDate(value ? value.format('YYYY-MM-DD') : null);
    setIsDisabled(false);
  };

  const onConfirmSetVisible = (value, name) => {
    if (name == 'Opening Date') setOpeningDateConfirmVisible(value);
    if (name == 'Encompass Launch Date') setEncompassLaunchDateConfirmVisible(value);
  };

  const onRegionSelect = (value) => {
    setRegionId(value);
    resetDistrict();
  };

  const onDistrictSelect = (value) => {
    setDistrictId(value);
  };

  const onStatusSelect = (value) => {
    setStatusValue(value);
  };

  const onLocationCategorySelect = (value) => {
    setLocationCategory(value);
  };

  const onPOSSelect = (value) => {
    setpos(value);
  };

  const onYearUpdate = (value) => {
    form.setFieldsValue({ vintageName: value.currentTarget.value + ' Acquisition' });
  };

  const content = (key: any, value: any, data: any) => {
    const checkboxProp: any = (key === 'dryEyeTreatment' || key === 'ctc') && { valuePropName: 'checked' };
    const formItemParams = {
      ...value,
      data: data(),
      ...(key === 'region' && { onSelect: onRegionSelect }),
      ...(key === 'district' && { onSelect: onDistrictSelect }),
      ...(key === 'status' && { onSelect: onStatusSelect, data: Object.entries(StatusesEnum) }),
      ...(key === 'locationCategory' && {
        onSelect: onLocationCategorySelect,
        data: Object.entries(LocationCategory),
      }),
      ...(key === 'acquisitionYear' && { onBlur: onYearUpdate }),
      ...(key === 'pos' && {
        onSelect: onPOSSelect,
        data: Object.entries(PosEnum),
      }),
      ...(key === 'openingDate' && {
        onSelect: onOpeningDateSelect,
        data: openingDate,
        onConfirmVisible: onConfirmSetVisible,
        isConfirmVisible: isOpeningDateConfirmVisible,
        name: value.label,
      }),
      ...(key === 'encompassLaunchDate' && {
        onSelect: onEncompassLaunchDateSelect,
        data: encompassLaunchDate,
        onConfirmVisible: onConfirmSetVisible,
        isConfirmVisible: isEncompassLaunchDateConfirmVisible,
        name: value.label,
      }),
      allowDateClear: allowDateClear,
    };

    const rules =
      key === 'region' || key === 'district'
        ? [{ required: true, message: `Please select ${capitalize(key)}` }]
        : value.rules;

    if (value.isVisible && ((isCreateMode && value.add) || (!isCreateMode && value.edit)))
      return (
        <Form.Item
          key={key}
          label={value.label}
          name={key}
          rules={rules}
          validateTrigger={['onBlur', 'onSubmit']}
          className={`${classNameForm}-${key}`}
          {...checkboxProp}
        >
          {formItemMap(formItemParams, !isEditAllowed)}
        </Form.Item>
      );
    else if (
      (key === 'openingDate' && statusValue === 'HOLD') ||
      (key === 'newOfficeYear' && locationCategory === 'DENOVO') ||
      (key === 'jointVentureYear' && locationCategory === 'JOINT_VENTURE') ||
      (key === 'acquisitionYear' && locationCategory === 'ACQUISITION') ||
      (key === 'preAcquisitionAnnualRevenue' &&
        (locationCategory === 'ACQUISITION' || locationCategory === 'JOINT_VENTURE')) ||
      (key === 'vintageName' && locationCategory === 'ACQUISITION') ||
      (key === 'encompassLaunchDate' && pos === 'ENCOMPASS')
    ) {
      return (
        <Form.Item
          key={key}
          label={value.label}
          name={key}
          rules={value.rules}
          validateTrigger={['onBlur', 'onSubmit']}
          className={`${classNameForm}-${key}`}
        >
          {formItemMap(formItemParams, !isEditAllowed)}
        </Form.Item>
      );
    } else return null;
  };
  return (
    <>
      {isCreateMode ? (
        <Form
          form={form}
          name={name}
          layout='vertical'
          onFinish={onSubmit}
          autoComplete='off'
          className={classNameForm}
          onValuesChange={() => setIsDisabled(false)}
        >
          <Tabs defaultActiveKey='1' type='card' destroyInactiveTabPane={false} animated={false}>
            <Tabs.TabPane tab='Location' key='1'>
              {Object.entries(locationConfig(isCreateMode)).map(([key, value]: [string, any]) => {
                const data = () => {
                  switch (key) {
                    case 'region':
                      return regions;
                    case 'district':
                      return districts;
                    case 'status':
                      return Object.entries(StatusesEnum);
                  }
                };

                // Render City / State / ZIP in a single row
                if (['city', 'state', 'zip'].includes(key)) {
                  if (key === 'city') {
                    return (
                      <Row
                        key='city-state-zip'
                        gutter={0} // remove default gutter
                        justify='start'
                        style={{ marginBottom: 3 }}
                      >
                        <Col span={8} style={{ paddingRight: 4 }}>
                          <Form.Item label='City' name='city'>
                            <Input />
                          </Form.Item>
                        </Col>

                        <Col span={8} style={{ paddingLeft: 2, paddingRight: 2 }}>
                          <Form.Item
                            label='State'
                            name='state'
                            rules={[{ required: true, message: 'Please enter state' }]}
                          >
                            <Input />
                          </Form.Item>
                        </Col>

                        <Col span={8} style={{ paddingLeft: 4 }}>
                          <Form.Item label='ZIP' name='zip'>
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>
                    );
                  }
                  return null; // skip individual rendering of state and zip
                }

                return content(key, value, data);
              })}
            </Tabs.TabPane>
            <Tabs.TabPane
              forceRender
              tab={
                <span>
                  Managers & Partners
                  <span
                    style={{
                      color: 'red',
                      fontSize: '18px',
                      marginLeft: '1px',
                      top: '2px',
                    }}
                  >
                    {' '}
                    *
                  </span>
                </span>
              }
              key='2'
            >
              <Form.Item
                key='districtManager'
                label='District Manager'
                name='districtManager'
                rules={[{ required: true, message: 'Please select District Manager' }]}
              >
                <AutoComplete
                  allowClear
                  placeholder='Select District Manager'
                  options={contacts.map((c) => ({
                    label: `${c.name} (${c.email})`,
                    value: `${c.name} (${c.email})`,
                    contact: c,
                  }))}
                  filterOption={(inputValue, option: any) =>
                    option.label.toUpperCase().includes(inputValue.toUpperCase())
                  }
                  onSelect={(value, option) => {
                    setIsValidSelection(true);
                    setDistrictManager(option.contact);
                    dispatch(fetchContacts({ name: '' }));
                  }}
                  onChange={(value) => {
                    setIsValidSelection(false);
                    if (!value) {
                      setDistrictManager(null);
                      return;
                    }
                    dispatch(fetchContacts({ name: value }));
                  }}
                  onBlur={() => {
                    if (!isValidSelection) {
                      form.setFieldsValue({ districtManager: null });
                      setDistrictManager(null);
                    }
                  }}
                />
              </Form.Item>
              <Form.Item
                key='regionalVicePresident'
                name='regionalVicePresident'
                label='Regional Vice President'
                rules={[{ required: true, message: 'Please select Regional Vice President' }]}
              >
                <AutoComplete
                  allowClear
                  placeholder='Select Regional Vice President'
                  options={contacts.map((c) => ({
                    label: `${c.name} (${c.email})`,
                    value: `${c.name} (${c.email})`,
                    contact: c,
                  }))}
                  filterOption={(inputValue, option: any) =>
                    option.label.toUpperCase().includes(inputValue.toUpperCase())
                  }
                  onSelect={(value, option) => {
                    setIsValidRVPSelection(true);
                    setRegionalVicePresident(option.contact);
                    dispatch(fetchContacts({ name: '' }));
                  }}
                  onChange={(value) => {
                    setIsValidRVPSelection(false);
                    if (!value) setRegionalVicePresident(null);
                    else dispatch(fetchContacts({ name: value }));
                  }}
                  onBlur={() => {
                    if (!isValidRVPSelection) {
                      form.setFieldsValue({ regionalVicePresident: null });
                      setRegionalVicePresident(null);
                    }
                  }}
                />
              </Form.Item>

              <Form.Item
                key='hrBusinessPartner'
                label='HR Business Partner'
                name='hrBusinessPartner'
                rules={[{ required: true, message: 'Please select HR Business Partner' }]}
              >
                <AutoComplete
                  allowClear
                  placeholder='Select HR Business Partner'
                  options={contacts.map((c) => ({
                    label: `${c.name} (${c.email})`,
                    value: `${c.name} (${c.email})`,
                    contact: c,
                  }))}
                  filterOption={(inputValue, option: any) =>
                    option.label.toUpperCase().includes(inputValue.toUpperCase())
                  }
                  onSelect={(value, option) => {
                    setIsValidHRSelection(true);
                    setHrBusinessPartner(option.contact);
                    dispatch(fetchContacts({ name: '' }));
                  }}
                  onChange={(value) => {
                    setIsValidHRSelection(false);
                    if (!value) setHrBusinessPartner(null);
                    else dispatch(fetchContacts({ name: value }));
                  }}
                  onBlur={() => {
                    if (!isValidHRSelection) {
                      form.setFieldsValue({ hrBusinessPartner: null });
                      setHrBusinessPartner(null);
                    }
                  }}
                />
              </Form.Item>
            </Tabs.TabPane>
          </Tabs>
          {/* SAVE BUTTON VISIBLE FOR BOTH TABS */}
          <div style={{ marginTop: 16, textAlign: 'right' }}>
            <Button type='primary' htmlType='submit' disabled={disCheck} className='btn-form-action'>
              SAVE
            </Button>
          </div>
        </Form>
      ) : (
        <Form
          form={form}
          name={name}
          layout='vertical'
          onFinish={onSubmit}
          autoComplete='off'
          className={classNameForm}
          onValuesChange={() => setIsDisabled(false)}
        >
          {Object.entries(locationConfig(isCreateMode)).map(([key, value]: [string, any]) => {
            const data = () => {
              switch (key) {
                case 'region':
                  return regions;
                case 'district':
                  return districts;
                case 'status':
                  return Object.entries(StatusesEnum);
              }
            };
            return content(key, value, data);
          })}

          <Form.Item>
            <Button type='primary' htmlType='submit' disabled={disCheck} className='btn-form-action'>
              SAVE
            </Button>
          </Form.Item>
        </Form>
      )}
    </>
  );
}

export default LocationDetails;
